import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconArrowLeft, IconReplace } from '@tabler/icons-react'
import { useHistory, useParams } from 'react-router-dom'
import { isEmpty, startCase, toArray } from 'lodash'

import { Toggle } from '@/components/forms/Checkbox'
import {
  createScorecardConfig,
  toggleActivateScorecardConfig,
  updateScorecardConfig,
} from '@/reducers/qa-copilot/qa-copilot.actions'
import { openModal } from '@/reducers/ui/ui.redux'

import { QACopilotScorecardVersionSelector } from '@/views/QACopilot/components/QACopilotScorecardVersionSelector'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { Card } from '@/components/cards/Card'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Pill } from '@/components/pills/Pill'
import { formatTime } from '@/utils/helpers'
import { Button } from '@/components/buttons/Button'
import { CopyButton } from '@/components/buttons/CopyButton'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { promptHasChangesHelper } from '@/reducers/prompts/prompts.helpers'

import { QACopilotScorecardName } from './QACopilotScorecardName'

const fieldNameMap = {
  active: 'Active',
  agent_viewable: 'Visible to Agents',
  call_id: 'IDs',
  call_type: 'Channel',
  criteria_prompt: 'Scoring Criteria Prompt',
  dispositions: 'Dispositions',
  dispositions_inclusive: 'Dispositions Inclusive',
  exceeds_threshold: 'Exceeds (%)',
  expected_response: 'Expected Response',
  fallback_initial_model_id: 'Fallback Model',
  first_x_minutes: 'Specific Timeframes - at start',
  ignore_scored_status: 'Ignore Partially Scored',
  improvement_threshold: 'Below (%)',
  last_x_minutes: 'Specific Timeframes - at end',
  llm_model_id: 'AI Model',
  max_duration_in_minutes: 'Call Length Max',
  max_message_count: 'Message Count Max',
  meets_threshold: 'Meets (%)',
  min_message_count: 'Message Count Min',
  model_id: 'AI Model',
  multicall: 'Multi-Call Scoring',
  multicall_same_user: 'Multi-Call Grouping',
  organization_id: 'Organization',
  playbooks: 'Playbook matches',
  required_manual_task_count: 'Manual Scoring Goal',
  scores_to_fallback: 'Scores to Fallback',
  shell_prompt: 'Shell Prompt',
  should_eligibility_respond_yes: 'Eligibility Expected Response',
  side: 'Side to include in transcript',
  tags: 'Tags',
  tags_inclusive: 'Tags Inclusive',
  user_prompt: 'Eligibility Prompt',
  users: 'Agents',
  users_inclusive: 'Agents Inclusive',
  voip_campaigns: 'VoIP Campaigns',
  voip_campaigns_inclusive: 'VoIP Campaigns Inclusive',
}

const formatArray = ({ array, maxStringLength }) => {
  const sortedArray = Array.from(array).sort((a, b) => a - b)
  const itemsToDisplay = []
  let currLength = 0
  let currIndex = 0

  while (
    currIndex < sortedArray.length &&
    currLength + sortedArray[currIndex].length < maxStringLength
  ) {
    itemsToDisplay.push(sortedArray[currIndex])
    currLength += sortedArray[currIndex].length
    ++currIndex
  }
  const hiddenItems = sortedArray.slice(currIndex)
  return { itemsToDisplay, hiddenItems }
}

const formatAddedOrRemovedItemsChange = ({ array, actionText, maxStringLength }) => {
  const { itemsToDisplay, hiddenItems } = formatArray({ array, maxStringLength })
  return (
    <>
      {`${actionText} [${itemsToDisplay.join(', ')}]`}
      {hiddenItems.length > 0 && (
        <InteractiveTooltip
          content={
            <div className="qa-copilot-history-change-log__tooltip-content">
              {hiddenItems.join('\n')}
            </div>
          }
          placement="top"
        >
          <Pill small className="qa-copilot-history-change-log__pill" data-testid="viewers-pill">
            +{hiddenItems.length} more
          </Pill>
        </InteractiveTooltip>
      )}
    </>
  )
}

const formatAndReturnStringValue = ({ value, maxStringLength }) => {
  if (value === null) {
    return 'N/A'
  }

  if (Array.isArray(value)) {
    const { itemsToDisplay } = formatArray({ array: value, maxStringLength })
    return itemsToDisplay.join(', ')
  }

  return String(value)
}

const formatWhatChangedContent = ({ oldValue, newValue, isPopUp }) => {
  if (isPopUp) {
    return `${oldValue}\n→\n${newValue}`
  }
  return `${oldValue} → ${newValue}`
}

const formatUpdateChange = ({
  oldValue,
  newValue,
  fieldName,
  isPromptChange,
  canEditPrompts,
  maxStringLength,
}) => {
  if (isPromptChange && !canEditPrompts) {
    return fieldName === 'criteria_prompt'
      ? 'The scoring criteria prompt changed'
      : 'The eligibility prompt changed'
  }
  const showContentInPopUp = String(oldValue).length + String(newValue).length > maxStringLength
  const fieldNameText = fieldNameMap[fieldName]
  const formattedFieldName = fieldNameText ?? fieldName
  const formattedOldValue = formatAndReturnStringValue({
    value: oldValue,
    maxStringLength,
  })
  const formattedNewValue = formatAndReturnStringValue({
    value: newValue,
    maxStringLength,
  })

  return (
    <div>
      <span className="qa-copilot-history-change-log__field-name">{formattedFieldName}</span>
      <div className="qa-copilot-history-change-log__value-change-row">
        {!showContentInPopUp ? (
          <>
            {formatWhatChangedContent({
              oldValue: formattedOldValue,
              newValue: formattedNewValue,
              isPopUp: false,
            })}
          </>
        ) : (
          <InteractiveTooltip
            interactive
            content={
              <div className="qa-copilot-history-change-log__tooltip-content">
                {isPromptChange && (
                  <div className="qa-copilot-history-change-log__copy-row">
                    <CopyButton content={formattedNewValue} brand small />
                  </div>
                )}
                <div className="qa-copilot-history-change-log__scroll">
                  {formatWhatChangedContent({
                    oldValue: formattedOldValue,
                    newValue: formattedNewValue,
                    isPopUp: true,
                  })}
                </div>
              </div>
            }
            placement="top"
          >
            <Pill small className="qa-copilot-history-change-log__pill" data-testid="viewers-pill">
              show details
            </Pill>
          </InteractiveTooltip>
        )}
      </div>
    </div>
  )
}

const buildWhatChangedString = (change, canEditPrompts) => {
  const maxStringLength = 30
  const isErrorRestoreVersion = change.event_type.toLowerCase().includes('error_restore_version')
  if (isErrorRestoreVersion) {
    return 'Error restoring version'
  }

  const isRestoreVersionEvent = change.event_type.toLowerCase().includes('restore_version')
  if (isRestoreVersionEvent) {
    return (
      <div>
        <span>Restored version:</span> <span>{formatTime(change.restored_version)}</span>
      </div>
    )
  }

  const hasItemNames = !isEmpty(change.item_names)
  const isAddedEvent = change.event_type.toLowerCase().includes('added') && hasItemNames
  const isRemoveEvent = change.event_type.toLowerCase().includes('removed') && hasItemNames
  if (isAddedEvent || isRemoveEvent) {
    const actionText = isAddedEvent ? 'Added' : 'Removed'
    return formatAddedOrRemovedItemsChange({
      array: change.item_names,
      actionText,
      maxStringLength,
    })
  }

  const isUpdateEvent = change.event_type.toLowerCase().includes('updated')
  if (isUpdateEvent) {
    const isPromptChange = change.event_type.toLowerCase().includes('prompt')
    return formatUpdateChange({
      oldValue: change.old_value,
      newValue: change.new_value,
      fieldName: change.field_name,
      isPromptChange,
      canEditPrompts,
      maxStringLength,
    })
  }

  return ''
}

const columns = [
  {
    accessor: 'change',
    label: 'What changed',
    isSearchable: true,
  },
  {
    accessor: 'event_type',
    label: 'Event Type',
    collapsing: true,
    isSearchable: true,
  },
  {
    accessor: 'author_full_name',
    label: 'Who changed it',
    isSearchable: true,
  },
  {
    accessor: 'created_at',
    label: 'When it happened',
    isSearchable: true,
  },
]

export const QACopilotNavigation = ({ isOutdatedScorecard, scorecardType = 'copilot', url }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { scorecardConfig, scorecardConfigChanges, dirty, loading } = useSelector(
    (state) => state.qaCopilot
  )
  const { prompts, pristinePrompts } = useSelector((state) => state.prompts)
  const { id } = useParams()
  const noCriteria = isEmpty(
    toArray(scorecardConfig?.sections).flatMap((section) =>
      toArray(section.measures).flatMap((measure) => toArray(measure.criteria))
    )
  )
  const criteriaPointsModalId = 'qaCopilot/criteriaPoints'
  const settingsModalId = 'qaCopilot/settings'
  const { organizationid: currentUserOrgId, prompt_editor: isPromptEditor } = useSelector(
    (state) => state.currentUser
  )
  const isBaltoAdmin = currentUserOrgId === 1

  const canSaveScorecard = () => {
    if (isOutdatedScorecard) {
      return false
    }

    if (!dirty) {
      return 'Make changes to save'
    }

    if (!scorecardConfig.organization_id) {
      return 'Select an organization to save'
    }

    // Validate that AI criteria have valid prompts
    const allCriteria = toArray(scorecardConfig?.sections).flatMap((section) =>
      toArray(section.measures).flatMap((measure) => toArray(measure.criteria))
    )

    const criteriaRequiringPrompts = allCriteria.filter(
      (criteria) =>
        criteria.criteria_type === 'ai' ||
        (criteria.criteria_type === 'manual' && criteria.manual_timestamp_markers)
    )

    const criteriaWithoutPrompts = criteriaRequiringPrompts.filter((criteria) => {
      // Check if criteria has a prompt_id OR a prompt in Redux with matching scid
      const hasExistingPrompt = criteria.prompt_id != null
      const hasLocalPrompt = prompts.some(
        (prompt) => prompt.scid === criteria.scid && !isEmpty(prompt.user_prompt)
      )
      return !hasExistingPrompt && !hasLocalPrompt
    })

    if (!isEmpty(criteriaWithoutPrompts)) {
      const count = criteriaWithoutPrompts.length
      const noun = count === 1 ? 'criterion needs a prompt' : 'criteria need prompts'
      return `${count} AI ${noun} to be configured`
    }

    // The API has no model to run a prompt on without an llm_model_id, so the save refuses
    // one. The criteria and settings forms require it, but the bulk paths — CSV
    // auto-populate, guided setup — mint prompts without a form, and they come out
    // modelless whenever the model catalog didn't load. Blocking here beats a failed save
    // on submit.
    //
    // Scoped to the prompts the save would actually write, exactly as the save-side guard
    // is: an untouched prompt is never POSTed, so a legacy one whose model name never
    // resolved to an id must not block a save that leaves it alone.
    const promptsMissingModel = prompts.filter(
      (prompt) =>
        prompt.scid && !prompt.llm_model_id && promptHasChangesHelper(prompt, pristinePrompts)
    )

    if (!isEmpty(promptsMissingModel)) {
      const count = promptsMissingModel.length
      const noun = count === 1 ? 'prompt needs' : 'prompts need'
      return `${count} ${noun} an AI model selected`
    }

    return false
  }

  const handleGoBack = () => {
    history.push(url)
  }

  const handleOpenCriteriaPointsModal = () => {
    dispatch(openModal(criteriaPointsModalId))
  }

  const handleOpenSettingsModal = () => {
    dispatch(openModal(settingsModalId))
  }

  const createOrSave = (config) => {
    if (id === 'create') {
      dispatch(createScorecardConfig(config, history, prompts))
    } else {
      const isRestoringVersion = scorecardConfig?.id !== scorecardConfig?.latest_scorecard_config_id
      dispatch(updateScorecardConfig(config, history, prompts, isRestoringVersion))
    }
  }

  const handleSave = () => {
    createOrSave({ ...scorecardConfig, type: scorecardType })
  }

  const handleSaveAndActivate = () => {
    createOrSave({ ...scorecardConfig, type: scorecardType, active: true })
  }

  const handleToggleActivate = () => {
    // Can't activate a scorecard that hasn't been created
    if (id === 'create') {
      return
    }

    // Save and activate/deactivate if dirty
    if (dirty) {
      createOrSave({ ...scorecardConfig, type: scorecardType, active: !scorecardConfig.active })
    } else {
      // Just activate/deactivate if not dirty
      dispatch(toggleActivateScorecardConfig({ ...scorecardConfig, type: scorecardType }))
    }
  }

  const scorecardConfigChangesRows = Array.isArray(scorecardConfigChanges)
    ? scorecardConfigChanges.map((change) => {
        const isErrorRestoreVersion = change.event_type
          .toLowerCase()
          .includes('error_restore_version')
        if (isErrorRestoreVersion) {
          return {
            ...change,
          }
        }
        const hasItemNames = !isEmpty(change.item_names)
        const isAddedEvent = change.event_type.toLowerCase().includes('added') && hasItemNames
        const isRemoveEvent = change.event_type.toLowerCase().includes('removed') && hasItemNames
        const isUpdateEvent = change.event_type.toLowerCase().includes('updated')
        const isRestoreVersionEvent = change.event_type.toLowerCase().includes('restore_version')

        return {
          ...change,
          change: buildWhatChangedString(change, isBaltoAdmin || isPromptEditor),
          created_at: formatTime(change.created_at),
          event_type: {
            value: change.event_type,
            as: (
              <Pill
                small
                critical={isErrorRestoreVersion}
                success={isAddedEvent || isRestoreVersionEvent}
                informative={isUpdateEvent}
                caution={isRemoveEvent}
              >
                {startCase(change.event_type.replace(/_/g, ' '))}
              </Pill>
            ),
          },
        }
      })
    : []

  return (
    <div className="qa-copilot-navigation">
      <div className="qa-copilot-navigation__container">
        <div className="qa-copilot-navigation__title">
          <Button square onClick={handleGoBack}>
            <IconArrowLeft />
          </Button>
          <QACopilotScorecardName isOutdatedScorecard={isOutdatedScorecard} />
        </div>
        <div className="qa-copilot-navigation__actions">
          <Button basic onClick={handleOpenSettingsModal} data-testid="qa-copilot-settings-button">
            Settings
          </Button>

          <Button
            basic
            data-testid="edit-points-button"
            onClick={handleOpenCriteriaPointsModal}
            disabled={noCriteria}
            disabledTooltip="Add criteria to edit points"
          >
            Points
          </Button>

          {id !== 'create' && !isEmpty(scorecardConfigChangesRows) && (
            <ButtonAndFormModal
              buttonLabel="Change History"
              icon={<IconReplace />}
              modalId="scorecard/audit"
              modalProps={{ size: 'large' }}
              modalTitle={`Scorecard Change History - ${scorecardConfig?.name}`}
              modalContent={
                <div className="modal-content-wrapper">
                  <Card className="no-padding table-segment">
                    <AdvancedTable
                      loading={loading.scorecardConfigChanges}
                      rows={scorecardConfigChangesRows}
                      columns={columns}
                      pagination
                      defaultOrder="desc"
                      sortEnabled={false}
                    />
                  </Card>
                </div>
              }
            />
          )}

          {id !== 'create' && <QACopilotScorecardVersionSelector />}

          <Button
            data-testid="save-button"
            brand={id !== 'create'}
            onClick={handleSave}
            disabled={!!canSaveScorecard()}
            disabledTooltip={canSaveScorecard()}
            tooltipProps={{ placement: 'bottom-end' }}
          >
            {isOutdatedScorecard ? 'Restore Scorecard' : 'Save'}
          </Button>

          {id === 'create' ? (
            <Button
              data-testid="save-and-activate-button"
              brand
              onClick={handleSaveAndActivate}
              disabled={!!canSaveScorecard()}
              disabledTooltip={canSaveScorecard()}
              tooltipProps={{ placement: 'bottom-end' }}
            >
              Save & Activate
            </Button>
          ) : (
            <InteractiveTooltip
              content={`Click to ${scorecardConfig?.active ? 'deactivate' : 'activate'} scorecard`}
              placement="bottom-end"
            >
              <Toggle
                data-testid="activate-scorecard-config"
                aria-label="Scorecard active"
                checked={scorecardConfig?.active}
                onChange={handleToggleActivate}
                style={{ marginLeft: '1rem' }}
                disabled={isOutdatedScorecard}
              />
            </InteractiveTooltip>
          )}
        </div>
      </div>
    </div>
  )
}
